import * as React from 'react';
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { LocalizerHelper } from "../../helpers/localizerHelper";
import { COLLECTIONS, DELIVERY_MODES } from "../../constants/data";
import Screen from '../screen';
import RestaurantHeader from "./RestaurantHeader";
import ChuzeatFooter from "../footer/ChuzeatFooter";
import { LoaderGif } from '../loader';
import './index.scss';
import { RootState } from '../../store';
import { LinkModel } from '../../features/link/link.types';
import { validateLinkSuccess, resetLink, selectLink } from '../../features/link/link.slice';
import { validateLinkService } from '../../features/link/link.service';
import { createNewSessionService } from '../../features/session/session.service';
import { setSessionId } from '../../features/session/session.slice';
import { Button } from '@material-ui/core';
import { setRestaurantId } from '../../features/restaurant/restaurant.slice';
import MenuChooser from '../menu_chooser/MenuChooser';
import { MenuFromApi } from '../../models/menu';
import { firestore } from '../../firebase';
import { setExternalTable } from '../../features/auth/auth.slice';

const Restaurant: React.FC = () => {
    const { link_alias: linkAlias } = useParams<{ link_alias: string }>();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const restaurantId = useSelector((state: RootState) => state.restaurant.restaurantId);
    const sessionId = useSelector((state: RootState) => state.session.sessionId);
    const links = useSelector((state: RootState) => state.link.links);
    const selectedLink = useSelector((state: RootState) => state.link.selectedLink);
    const restaurantLink = useSelector((state: RootState) => state.link.restaurant);
    const linkMenus = useSelector((state: RootState) => state.link.selectedLink?.menus ?? []);

    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [menuChooser, setMenuChooser] = useState(false);

    const lastLinkAlias = window.localStorage?.getItem('chuzeat_last_alias_link') ?? '';

    // Initialize link selection
    useEffect(() => {
        dispatch(resetLink());
    }, [dispatch]);

    // Validate link alias if present
    useEffect(() => {
        if (!linkAlias) return;
        validateLink(linkAlias);
    }, [dispatch, linkAlias]);
    
    // Stop initial loading once links arrive
    useEffect(() => {
        if (links) setInitialLoading(false);
    }, [links]);

    // dispatch restaurant slice
    React.useEffect(() => {
        if (!restaurantLink) return;
        dispatch(setRestaurantId(restaurantLink));
    }, [dispatch, restaurantLink]);

    const validateLink = async (linkAlias: string) => {
        window.localStorage?.setItem('chuzeat_last_alias_link', linkAlias);
        const result = await validateLinkService(linkAlias);
        dispatch(validateLinkSuccess(result));
    };

    const createNewDitsIfNeeded = async (link: LinkModel) => {
        if (!restaurantId) return;
        setLoading(true);
        const alias = linkAlias ?? lastLinkAlias;
        dispatch(selectLink(link));
        try{
            const sessionId = await createNewSessionService(link.id);
            const sessionRef = firestore.doc(`${COLLECTIONS.restaurants}/${restaurantId}/${COLLECTIONS.dineInTableSitting}/${sessionId}`);
            dispatch(setExternalTable(sessionRef.path));
            dispatch(setSessionId(sessionId));
            const linkMenus = link.menus ?? [];
            if (linkMenus.length > 0) {
                const firstMenu = linkMenus[0];
                const isDelivery = link.service_mode === DELIVERY_MODES.delivery;
                if (linkMenus.length === 1) {
                    if (isDelivery) {
                        navigate(`/menu/${alias}/location-service-available/${restaurantId}/${sessionId}/${firstMenu.docId}`);
                    } else {
                        navigate(`/menu/${alias}/menu/${restaurantId}/${sessionId}/${firstMenu.docId}`);
                    }
                } else {
                    setLoading(false);
                    setMenuChooser(true);
                }
            }else{
                setLoading(false);
            }
        }catch(e){
            alert(e);
            setLoading(false);
        }
    };

    const _goToMenu = (menu: MenuFromApi) => {
        const isDelivery = selectedLink?.service_mode === DELIVERY_MODES.delivery;
        const alias = linkAlias ?? lastLinkAlias;
        if (isDelivery) {
            navigate(`/menu/${alias}/location-service-available/${restaurantId}/${sessionId}/${menu.docId}`);
        } else {
            navigate(`/menu/${alias}/menu/${restaurantId}/${sessionId}/${menu.docId}`);
        }
    }

    const renderLoading = () => (
        <LoaderGif
            image="/assets/loading_menus.gif"
            message={LocalizerHelper.localized('loading_msg_retrieve_menus')}
        />
    );

    const renderMenuChooser = () => (
        <MenuChooser goToMenu={_goToMenu} menus={linkMenus}></MenuChooser>
    );

    const renderRestaurantScreen = () => (
        <div className="content_container with_footer">
            {links && links.length > 0 ? (
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <p className="user_questions">{LocalizerHelper.localized('link_chooser_header')}</p>
                        </div>
                    </div>
                    <div className="container-buttons-action">
                        <div className="container">
                            {links.map(link => (
                                <div className="row" key={link.id}>
                                    <div className="col-md-12">
                                        <Button
                                            className="accent-button full-width button"
                                            onClick={() => createNewDitsIfNeeded(link)}
                                        >
                                            {LocalizerHelper.localized(link, ['name'])}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className='container'>
                    <p className="user_questions">{LocalizerHelper.localized('no_dining_tables_availables')}</p>
                </div>
            )}
        </div>
    )

    const renderContent = () => {
        if (initialLoading || loading) return renderLoading();
        if(menuChooser) return renderMenuChooser();
        return renderRestaurantScreen();
    }

    return <Screen className="screen restaurant-screen">
        <RestaurantHeader className='full-width' showBack={false} showCart={false} />
            {renderContent()}
        <ChuzeatFooter />
    </Screen>;
};

export default Restaurant;
